import { useRef, useState } from "react";
import { Upload, X, FileText, Image as ImageIcon, FileVideo, File as FileIcon } from "lucide-react";
import { uploadFile } from "@/lib/api";
import { toast } from "sonner";

const ACCEPT = ".jpg,.jpeg,.png,.gif,.webp,.pdf,.mp4,.mov,.avi,.doc,.docx";

function iconFor(filename = "") {
  const ext = filename.split(".").pop()?.toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return <ImageIcon size={18} />;
  if (["mp4", "mov", "avi"].includes(ext)) return <FileVideo size={18} />;
  if (ext === "pdf") return <FileText size={18} />;
  return <FileIcon size={18} />;
}

export default function FileUpload({ onChange, label = "Upload files", testId = "file-upload" }) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]); // {id, filename, size}
  const [uploading, setUploading] = useState(false);

  async function handleFiles(filesList) {
    const arr = Array.from(filesList);
    setUploading(true);
    const results = [...files];
    for (const f of arr) {
      try {
        const data = await uploadFile(f);
        results.push(data);
      } catch (e) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setFiles(results);
    onChange?.(results.map((r) => r.id));
    setUploading(false);
  }

  function removeAt(idx) {
    const next = files.filter((_, i) => i !== idx);
    setFiles(next);
    onChange?.(next.map((r) => r.id));
  }

  return (
    <div data-testid={testId}>
      <label className="field-label">{label}</label>
      <div
        className="border-2 border-dashed border-[#E2DCD0] bg-white p-6 text-center cursor-pointer hover:border-[#064E2E] transition-colors"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files) handleFiles(e.dataTransfer.files); }}
        data-testid={`${testId}-dropzone`}
      >
        <Upload className="mx-auto text-[#064E2E] mb-2" size={26} />
        <p className="text-sm text-[#071A33]">
          {uploading ? "Uploading…" : "Drag files here or click to upload"}
        </p>
        <p className="text-xs text-[#475569] mt-1">Images, videos, PDF, DOC, DOCX (max 50MB each)</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
          className="hidden"
          data-testid={`${testId}-input`}
        />
      </div>

      {files.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid={`${testId}-list`}>
          {files.map((f, i) => (
            <li
              key={f.id}
              className="flex items-center justify-between bg-white border border-[#E2DCD0] px-3 py-2 text-sm"
            >
              <span className="flex items-center gap-2 text-[#071A33] truncate">
                {iconFor(f.filename)}
                <span className="truncate max-w-[260px]">{f.filename}</span>
                <span className="text-xs text-[#475569]">{(f.size / 1024).toFixed(0)} KB</span>
              </span>
              <button
                type="button"
                className="text-[#475569] hover:text-[#064E2E]"
                onClick={() => removeAt(i)}
                aria-label="Remove file"
                data-testid={`${testId}-remove-${i}`}
              >
                <X size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
